import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MoreVertical, Pencil, Trash2, PackageCheck, RotateCcw, Sparkles } from "lucide-react";
import api, { apiError, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const FALLBACK = PLACEHOLDER_IMG;

const STATUS_CLS = {
  active: "bg-brand-green/10 text-brand-green",
  unavailable: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  sold: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  pending_review: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
};

export function ListingStatusBadge({ status, id }) {
  return (
    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[status] || "bg-secondary text-muted-foreground"}`} data-testid={id ? `my-listing-status-${id}` : undefined}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

export function EditListingDialog({ listing, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(listing);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(listing); }, [listing]);
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: f.title, price: parseFloat(f.price) || 0, negotiable: !!f.negotiable,
        description: f.description || "", condition: f.condition || "Used",
        category: f.category, state: f.state, suburb: f.suburb || null, photos: listing.photos || [],
      };
      const { data } = await api.put(`/marketplace/${listing.id}`, body);
      toast.success("Listing updated");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-listing-modal">
        <DialogHeader><DialogTitle>Edit listing</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="edit-title" /></div>
          <div className="flex gap-2">
            <div className="flex-1"><Label>Price ($)</Label><Input type="number" value={f.price ?? 0} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="edit-price" /></div>
            <div className="flex-1"><Label>Condition</Label><Input value={f.condition || ""} onChange={(e) => setF({ ...f, condition: e.target.value })} data-testid="edit-condition" /></div>
          </div>
          <div><Label>Category</Label><Input value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="edit-category" /></div>
          <div><Label>Description</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="edit-description" /></div>
          <div><Label>Suburb</Label><Input value={f.suburb || ""} onChange={(e) => setF({ ...f, suburb: e.target.value })} data-testid="edit-suburb" /></div>
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="edit-cancel">Cancel</Button>
          <Button className="flex-1" onClick={save} disabled={saving} data-testid="edit-save">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteListingDialog({ open, onOpenChange, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-listing-modal">
        <DialogHeader>
          <DialogTitle>Delete this listing?</DialogTitle>
          <DialogDescription>This will permanently remove this listing from Mtaa. This action cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="delete-cancel">Cancel</Button>
          <Button variant="destructive" className="flex-1" onClick={onConfirm} disabled={loading} data-testid="delete-confirm">{loading ? "Deleting…" : "Delete listing"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeatureListingDialog({ listing, open, onOpenChange }) {
  const [packages, setPackages] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/featured-packages").then(({ data }) => { setPackages(data.items); setSel(data.items[0]?.id || null); }).catch(() => {});
    api.get(`/marketplace/${listing.id}/featured-stats`).then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, [open, listing.id]);
  const active = stats?.featured;
  const buy = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/featured/checkout", {
        listing_id: listing.id, package_id: sel, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="feature-listing-modal">
        <DialogHeader>
          <DialogTitle>Feature this listing</DialogTitle>
          <DialogDescription>Give your listing extra visibility near the top of relevant marketplace results. Optional — your listing stays live either way.</DialogDescription>
        </DialogHeader>
        {active && stats && (
          <div className="rounded-xl border border-brand-green/30 bg-brand-green/5 p-3 text-sm" data-testid="feature-active-stats">
            <p className="font-semibold text-brand-green">Currently Featured until {new Date(stats.featured_until).toLocaleDateString("en-AU")}</p>
            <p className="mt-1 text-muted-foreground">{stats.stats.views || 0} views · {stats.stats.impressions || 0} impressions · {stats.stats.clicks || 0} clicks while Featured.</p>
          </div>
        )}
        <div className="space-y-2">
          {packages.map((p) => (
            <button key={p.id} onClick={() => setSel(p.id)} data-testid={`feature-pkg-${p.id}`}
              className={`flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left transition-colors ${sel === p.id ? "border-brand-green bg-brand-green/10" : "border-border hover:border-brand-green/40"}`}>
              <span className="text-sm font-medium">{p.name}<span className="ml-2 text-xs text-muted-foreground">{p.duration_days} days</span></span>
              <span className="font-display font-bold text-brand-green">${(p.price / 100).toFixed(2)}</span>
            </button>
          ))}
          {packages.length === 0 && <p className="text-sm text-muted-foreground">No featured packages are available right now.</p>}
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Close</Button>
          <Button className="flex-1" onClick={buy} disabled={busy || !sel} data-testid="feature-buy-btn">{busy ? "Redirecting…" : active ? "Extend feature" : "Feature listing"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ManageMenu({ listing, onChanged, onDeleted, buttonClassName }) {
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const [feature, setFeature] = useState(false);
  const [busy, setBusy] = useState(false);
  const isUnavailable = ["unavailable", "sold"].includes(listing.status);
  const toggle = async () => {
    try {
      await api.patch(`/marketplace/${listing.id}/status`, { status: isUnavailable ? "active" : "unavailable" });
      toast.success(isUnavailable ? "Marked as available" : "Marked as unavailable");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/marketplace/${listing.id}`);
      toast.success("Listing deleted.");
      setDel(false);
      onDeleted && onDeleted();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={buttonClassName} data-testid={`manage-listing-${listing.id}`}><MoreVertical className="h-4 w-4" />Manage listing</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setEdit(true)} data-testid={`manage-edit-${listing.id}`}><Pencil className="h-4 w-4" />Edit listing</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setFeature(true)} data-testid={`manage-feature-${listing.id}`}><Sparkles className="h-4 w-4" />Feature listing</DropdownMenuItem>
          <DropdownMenuItem onClick={toggle} data-testid={`manage-toggle-${listing.id}`}>
            {isUnavailable ? <><RotateCcw className="h-4 w-4" />Mark as available</> : <><PackageCheck className="h-4 w-4" />Mark as sold/unavailable</>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDel(true)} className="text-destructive focus:text-destructive" data-testid={`manage-delete-${listing.id}`}><Trash2 className="h-4 w-4" />Delete listing</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditListingDialog listing={listing} open={edit} onOpenChange={setEdit} onSaved={onChanged} />
      <FeatureListingDialog listing={listing} open={feature} onOpenChange={setFeature} />
      <DeleteListingDialog open={del} onOpenChange={setDel} onConfirm={doDelete} loading={busy} />
    </>
  );
}

export function ManageListingCard({ listing, onChanged, onDeleted }) {
  const l = listing;
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`my-listing-${l.id}`}>
      <Link to={`/listing/${l.id}`} className="relative block aspect-[4/3] bg-muted">
        <img src={fileUrl(l.photos?.[0]) || FALLBACK} alt={l.title} className="h-full w-full object-cover" />
        <span className="absolute left-2 top-2"><ListingStatusBadge status={l.status} id={l.id} /></span>
      </Link>
      <div className="space-y-2 p-3">
        <p className="font-display text-base font-bold text-brand-green">{l.price > 0 ? `$${l.price.toLocaleString()}` : "Free"}</p>
        <h3 className="line-clamp-1 text-sm font-medium text-foreground">{l.title}</h3>
        <ManageMenu listing={l} onChanged={onChanged} onDeleted={onDeleted} buttonClassName="w-full" />
      </div>
    </div>
  );
}
